import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { fitContain, overlayHasMark, regionEditPrompt } from "../lib/imageInspect";

function blankCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  return canvas;
}

function copyCanvas(source) {
  const copy = blankCanvas(source.width, source.height);
  copy.getContext("2d").drawImage(source, 0, 0);
  return copy;
}

function replaceCanvas(target, source) {
  target.width = source.width;
  target.height = source.height;
  target.getContext("2d").drawImage(source, 0, 0);
}

function turnedCanvas(source, turns) {
  const next = blankCanvas(turns % 2 ? source.height : source.width, turns % 2 ? source.width : source.height);
  const ctx = next.getContext("2d");
  ctx.translate(next.width / 2, next.height / 2);
  ctx.rotate((Math.PI / 2) * turns);
  ctx.drawImage(source, -source.width / 2, -source.height / 2);
  return next;
}

function flippedCanvas(source, axis) {
  const next = blankCanvas(source.width, source.height);
  const ctx = next.getContext("2d");
  if (axis === "x") {
    ctx.translate(next.width, 0);
    ctx.scale(-1, 1);
  } else {
    ctx.translate(0, next.height);
    ctx.scale(1, -1);
  }
  ctx.drawImage(source, 0, 0);
  return next;
}

function croppedCanvas(source, rect) {
  const x = Math.max(0, Math.min(source.width - 1, Math.round(rect.x)));
  const y = Math.max(0, Math.min(source.height - 1, Math.round(rect.y)));
  const width = Math.max(1, Math.min(source.width - x, Math.round(rect.w)));
  const height = Math.max(1, Math.min(source.height - y, Math.round(rect.h)));
  const next = blankCanvas(width, height);
  next.getContext("2d").drawImage(source, x, y, width, height, 0, 0, width, height);
  return next;
}

async function loadBitmap(path) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  try {
    const image = new Image();
    image.decoding = "async";
    const ready = new Promise((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Preview could not be loaded."));
    });
    image.src = url;
    await ready;
    const canvas = blankCanvas(image.naturalWidth || image.width, image.naturalHeight || image.height);
    canvas.getContext("2d").drawImage(image, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function ImageInspectViewport({
  path,
  comparePath = "",
  alt = "",
  canEdit = false,
  busy = false,
  onEditArea,
}) {
  const stageRef = useRef(null);
  const viewRef = useRef(null);
  const imageRef = useRef(null);
  const overlayRef = useRef(null);
  const compareRef = useRef(null);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const viewRefState = useRef({ ox: 0, oy: 0, scale: 1 });
  const dragRef = useRef(null);
  const zoomRef = useRef({ mode: "fit", custom: 1, panX: 0, panY: 0 });
  const [tool, setTool] = useState("pan");
  const [compare, setCompare] = useState("off");
  const [wipe, setWipe] = useState(0.5);
  const [brush, setBrush] = useState(28);
  const [note, setNote] = useState("");
  const [picked, setPicked] = useState("");
  const [crop, setCrop] = useState(null);
  const [textDraft, setTextDraft] = useState(null);
  const [status, setStatus] = useState("");
  const [ready, setReady] = useState(false);
  const [tick, setTick] = useState(0);
  const [history, setHistory] = useState({ undo: 0, redo: 0 });
  const redraw = () => setTick((value) => value + 1);

  const remember = () => {
    if (!imageRef.current || !overlayRef.current) return;
    undoRef.current.push({
      image: copyCanvas(imageRef.current),
      overlay: copyCanvas(overlayRef.current),
    });
    if (undoRef.current.length > 12) undoRef.current.shift();
    redoRef.current = [];
    setHistory({ undo: undoRef.current.length, redo: 0 });
  };

  const restore = (entry) => {
    replaceCanvas(imageRef.current, entry.image);
    replaceCanvas(overlayRef.current, entry.overlay);
    setCrop(null);
    redraw();
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setReady(false);
      setStatus("");
      setCompare("off");
      setCrop(null);
      setTextDraft(null);
      undoRef.current = [];
      redoRef.current = [];
      setHistory({ undo: 0, redo: 0 });
      zoomRef.current = { mode: "fit", custom: 1, panX: 0, panY: 0 };
      if (!path) return;
      try {
        const canvas = await loadBitmap(path);
        if (cancelled) return;
        imageRef.current = canvas;
        overlayRef.current = blankCanvas(canvas.width, canvas.height);
        setReady(true);
        redraw();
      } catch (err) {
        if (!cancelled) setStatus(err.message || "Preview could not be loaded.");
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [path]);

  useEffect(() => {
    let cancelled = false;
    compareRef.current = null;
    if (!comparePath) return undefined;
    loadBitmap(comparePath)
      .then((canvas) => {
        if (!cancelled) {
          compareRef.current = canvas;
          redraw();
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("The other iteration could not be loaded for compare.");
      });
    return () => {
      cancelled = true;
    };
  }, [comparePath]);

  useEffect(() => {
    const stage = stageRef.current;
    const view = viewRef.current;
    const image = imageRef.current;
    if (!stage || !view || !image) return undefined;
    const ctx = view.getContext("2d");
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (!width || !height) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    view.width = Math.round(width * dpr);
    view.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#050814";
    ctx.fillRect(0, 0, width, height);
    const zoom = zoomRef.current;
    const contain = fitContain(width, height, image.width, image.height);
    const scale = zoom.mode === "actual" ? 1 : zoom.mode === "custom" ? zoom.custom : contain;
    const drawPlaced = (bitmap, left, top, boxWidth, boxHeight) => {
      const local = fitContain(boxWidth, boxHeight, bitmap.width, bitmap.height);
      const dw = bitmap.width * local;
      const dh = bitmap.height * local;
      ctx.drawImage(bitmap, left + (boxWidth - dw) / 2, top + (boxHeight - dh) / 2, dw, dh);
    };
    if (compare === "side" && compareRef.current) {
      drawPlaced(compareRef.current, 0, 0, width / 2, height);
      drawPlaced(image, width / 2, 0, width / 2, height);
      if (overlayRef.current) drawPlaced(overlayRef.current, width / 2, 0, width / 2, height);
      ctx.strokeStyle = "rgba(255,255,255,.45)";
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.stroke();
      viewRefState.current = { ox: 0, oy: 0, scale: 1, compare: true };
      return undefined;
    }
    const dw = image.width * scale;
    const dh = image.height * scale;
    const ox = (width - dw) / 2 + zoom.panX;
    const oy = (height - dh) / 2 + zoom.panY;
    viewRefState.current = { ox, oy, scale, compare: false };
    if (compare === "wipe" && compareRef.current) {
      ctx.drawImage(compareRef.current, ox, oy, dw, dh);
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, width * wipe, height);
      ctx.clip();
      ctx.drawImage(image, ox, oy, dw, dh);
      if (overlayRef.current) ctx.drawImage(overlayRef.current, ox, oy, dw, dh);
      ctx.restore();
      ctx.strokeStyle = "#ffffff";
      ctx.beginPath();
      ctx.moveTo(width * wipe, 0);
      ctx.lineTo(width * wipe, height);
      ctx.stroke();
    } else {
      ctx.drawImage(image, ox, oy, dw, dh);
      if (overlayRef.current) ctx.drawImage(overlayRef.current, ox, oy, dw, dh);
    }
    if (crop && crop.w > 2 && crop.h > 2) {
      ctx.strokeStyle = "#ffd36a";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(ox + crop.x * scale, oy + crop.y * scale, crop.w * scale, crop.h * scale);
    }
    return undefined;
  }, [tick, compare, wipe, ready, crop]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => redraw());
    observer.observe(stage);
    return () => observer.disconnect();
  }, [ready]);

  const toImage = (event) => {
    const rect = viewRef.current.getBoundingClientRect();
    const { ox, oy, scale, compare: comparing } = viewRefState.current;
    if (comparing || !scale) return null;
    const x = (event.clientX - rect.left - ox) / scale;
    const y = (event.clientY - rect.top - oy) / scale;
    const image = imageRef.current;
    if (!image || x < 0 || y < 0 || x >= image.width || y >= image.height) return null;
    return { x, y };
  };

  const paintLine = (from, to, erasing) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext("2d");
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = brush;
    ctx.globalCompositeOperation = erasing ? "destination-out" : "source-over";
    ctx.strokeStyle = erasing ? "rgba(0,0,0,1)" : "rgba(255,59,107,0.55)";
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.restore();
    redraw();
  };

  const onPointerDown = (event) => {
    if (!ready || event.button !== 0 || compare !== "off") return;
    const point = toImage(event);
    if (tool === "pan" || !point) {
      dragRef.current = { kind: "pan", x: event.clientX, y: event.clientY, panX: zoomRef.current.panX, panY: zoomRef.current.panY };
      viewRef.current.setPointerCapture?.(event.pointerId);
      return;
    }
    if (tool === "drop") {
      const image = imageRef.current;
      const pixel = image.getContext("2d").getImageData(Math.floor(point.x), Math.floor(point.y), 1, 1).data;
      const hex = `#${[pixel[0], pixel[1], pixel[2]].map((value) => value.toString(16).padStart(2, "0")).join("")}`;
      setPicked(hex);
      setStatus(`Eyedropper ${hex}`);
      return;
    }
    if (tool === "text") {
      setTextDraft({ x: point.x, y: point.y, value: "" });
      return;
    }
    if (tool === "crop") {
      dragRef.current = { kind: "crop", start: point, point };
      setCrop({ x: point.x, y: point.y, w: 0, h: 0 });
      viewRef.current.setPointerCapture?.(event.pointerId);
      return;
    }
    remember();
    dragRef.current = { kind: tool, last: point };
    if (tool === "arrow") dragRef.current.start = point;
    else paintLine(point, point, tool === "erase");
    viewRef.current.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.kind === "pan") {
      zoomRef.current.panX = drag.panX + (event.clientX - drag.x);
      zoomRef.current.panY = drag.panY + (event.clientY - drag.y);
      if (zoomRef.current.mode === "fit") zoomRef.current.mode = "custom";
      if (zoomRef.current.mode !== "custom") zoomRef.current.custom = viewRefState.current.scale || 1;
      if (zoomRef.current.mode === "actual") {
        zoomRef.current.mode = "custom";
        zoomRef.current.custom = 1;
      }
      redraw();
      return;
    }
    const point = toImage(event);
    if (!point) return;
    if (drag.kind === "crop") {
      const x = Math.min(drag.start.x, point.x);
      const y = Math.min(drag.start.y, point.y);
      setCrop({ x, y, w: Math.abs(point.x - drag.start.x), h: Math.abs(point.y - drag.start.y) });
      return;
    }
    if (drag.kind === "mask" || drag.kind === "erase") {
      paintLine(drag.last, point, drag.kind === "erase");
      drag.last = point;
      return;
    }
    if (drag.kind === "arrow") drag.point = point;
  };

  const onPointerUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.kind !== "arrow" || !drag.start || !drag.point) return;
    const overlay = overlayRef.current;
    const ctx = overlay.getContext("2d");
    ctx.save();
    ctx.strokeStyle = "#ffd36a";
    ctx.fillStyle = "#ffd36a";
    ctx.lineWidth = Math.max(2, brush / 8);
    ctx.beginPath();
    ctx.moveTo(drag.start.x, drag.start.y);
    ctx.lineTo(drag.point.x, drag.point.y);
    ctx.stroke();
    const angle = Math.atan2(drag.point.y - drag.start.y, drag.point.x - drag.start.x);
    ctx.beginPath();
    ctx.moveTo(drag.point.x, drag.point.y);
    ctx.lineTo(drag.point.x - 14 * Math.cos(angle - 0.4), drag.point.y - 14 * Math.sin(angle - 0.4));
    ctx.lineTo(drag.point.x - 14 * Math.cos(angle + 0.4), drag.point.y - 14 * Math.sin(angle + 0.4));
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    redraw();
  };

  useEffect(() => {
    const node = viewRef.current;
    if (!node) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      const current = zoomRef.current;
      const base = current.mode === "actual" ? 1 : current.mode === "custom" ? current.custom : viewRefState.current.scale || 1;
      current.mode = "custom";
      current.custom = Math.min(8, Math.max(0.05, base * (event.deltaY < 0 ? 1.12 : 0.88)));
      redraw();
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [ready]);

  const bake = (nextImage, nextOverlay) => {
    remember();
    imageRef.current = nextImage;
    overlayRef.current = nextOverlay;
    setCrop(null);
    zoomRef.current = { mode: "fit", custom: 1, panX: 0, panY: 0 };
    redraw();
  };

  const applyCrop = () => {
    if (!crop || crop.w < 4 || crop.h < 4 || !imageRef.current) return;
    bake(croppedCanvas(imageRef.current, crop), croppedCanvas(overlayRef.current, crop));
    setStatus("Crop applied in this viewer. Download to keep the PNG.");
  };

  const commitText = () => {
    if (!textDraft?.value.trim() || !overlayRef.current) {
      setTextDraft(null);
      return;
    }
    remember();
    const ctx = overlayRef.current.getContext("2d");
    ctx.save();
    ctx.fillStyle = "#fff4c8";
    ctx.strokeStyle = "#1a1204";
    ctx.lineWidth = 3;
    ctx.font = `${Math.max(18, brush)}px sans-serif`;
    ctx.strokeText(textDraft.value.trim(), textDraft.x, textDraft.y);
    ctx.fillText(textDraft.value.trim(), textDraft.x, textDraft.y);
    ctx.restore();
    setTextDraft(null);
    redraw();
  };

  const download = () => {
    if (!imageRef.current) return;
    const out = copyCanvas(imageRef.current);
    out.getContext("2d").drawImage(overlayRef.current, 0, 0);
    const link = document.createElement("a");
    link.href = out.toDataURL("image/png");
    link.download = "forgelinc-image.png";
    link.click();
  };

  const editArea = () => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const marked = overlayHasMark(overlay.getContext("2d").getImageData(0, 0, overlay.width, overlay.height));
    if (!marked) {
      setStatus("Brush the region first. Edit this area uses Continue editing.");
      return;
    }
    onEditArea?.(regionEditPrompt(note));
  };

  const undo = () => {
    const entry = undoRef.current.pop();
    if (!entry || !imageRef.current) return;
    redoRef.current.push({ image: copyCanvas(imageRef.current), overlay: copyCanvas(overlayRef.current) });
    restore(entry);
    setHistory({ undo: undoRef.current.length, redo: redoRef.current.length });
  };

  const redo = () => {
    const entry = redoRef.current.pop();
    if (!entry || !imageRef.current) return;
    undoRef.current.push({ image: copyCanvas(imageRef.current), overlay: copyCanvas(overlayRef.current) });
    restore(entry);
    setHistory({ undo: undoRef.current.length, redo: redoRef.current.length });
  };

  const setZoomMode = (mode) => {
    zoomRef.current = { mode, custom: mode === "actual" ? 1 : zoomRef.current.custom, panX: 0, panY: 0 };
    redraw();
  };

  return (
    <div className="fld-img-host" data-testid="forgelink-image-stage">
      <div className="fld-img-tools">
        <button type="button" className="btn small" onClick={() => setZoomMode("fit")} data-testid="forgelink-image-fit">Fit</button>
        <button type="button" className="btn small" onClick={() => setZoomMode("actual")} data-testid="forgelink-image-actual">100%</button>
        <button type="button" className="btn small" onClick={() => setCompare((value) => (value === "off" ? "side" : value === "side" ? "wipe" : "off"))} disabled={!comparePath} data-testid="forgelink-image-compare">
          {compare === "off" ? "Compare" : compare === "side" ? "Side by side" : "Before / after"}
        </button>
        <button type="button" className="btn small" disabled={!ready || compare !== "off"} onClick={() => bake(turnedCanvas(imageRef.current, 1), turnedCanvas(overlayRef.current, 1))} data-testid="forgelink-image-rotate">Rotate</button>
        <button type="button" className="btn small" disabled={!ready || compare !== "off"} onClick={() => bake(flippedCanvas(imageRef.current, "x"), flippedCanvas(overlayRef.current, "x"))} data-testid="forgelink-image-flip">Flip</button>
        <button type="button" className={`btn small${tool === "mask" ? " primary" : ""}`} onClick={() => setTool("mask")} data-testid="forgelink-image-mask">Mask</button>
        <button type="button" className={`btn small${tool === "erase" ? " primary" : ""}`} onClick={() => setTool("erase")} data-testid="forgelink-image-erase">Eraser</button>
        <label className="fld-img-brush">
          Brush
          <input type="range" min="8" max="96" value={brush} onChange={(event) => setBrush(Number(event.target.value))} data-testid="forgelink-image-brush" />
        </label>
        <button type="button" className="btn small" disabled={!history.undo} onClick={undo} data-testid="forgelink-image-undo">Undo</button>
        <button type="button" className="btn small" disabled={!history.redo} onClick={redo} data-testid="forgelink-image-redo">Redo</button>
        <button type="button" className="btn small" disabled={!ready} onClick={download} data-testid="forgelink-image-download">Download PNG</button>
        <button type="button" className={`btn small${tool === "pan" ? " primary" : ""}`} onClick={() => setTool("pan")}>Pan</button>
      </div>
      {compare === "wipe" ? (
        <label className="fld-img-wipe">
          Before / after
          <input type="range" min="0" max="1" step="0.01" value={wipe} onChange={(event) => setWipe(Number(event.target.value))} data-testid="forgelink-image-wipe" />
        </label>
      ) : null}
      <div className="fld-img-stage" ref={stageRef}>
        <canvas
          ref={viewRef}
          aria-label={alt || "Selected iteration"}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        />
        {!ready ? <p className="fld-img-status">{status || "Loading render…"}</p> : null}
      </div>
      <div className="fld-img-tools fld-img-tools-sub">
        <button type="button" className={`btn small${tool === "crop" ? " primary" : ""}`} onClick={() => setTool("crop")} data-testid="forgelink-image-crop">Crop</button>
        {crop && tool === "crop" ? <button type="button" className="btn small primary" onClick={applyCrop} data-testid="forgelink-image-crop-apply">Apply crop</button> : null}
        <button type="button" className={`btn small${tool === "drop" ? " primary" : ""}`} onClick={() => setTool("drop")} data-testid="forgelink-image-eyedrop">Eyedropper</button>
        {picked ? <span className="fld-img-swatch" style={{ background: picked }} data-testid="forgelink-image-swatch">{picked}</span> : null}
        <button type="button" className={`btn small${tool === "text" ? " primary" : ""}`} onClick={() => setTool("text")} data-testid="forgelink-image-text">Text</button>
        <button type="button" className={`btn small${tool === "arrow" ? " primary" : ""}`} onClick={() => setTool("arrow")} data-testid="forgelink-image-arrow">Arrow</button>
        <input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Note for the brushed region"
          aria-label="Region edit note"
          data-testid="forgelink-image-region-note"
        />
        <button type="button" className="btn small primary" disabled={!canEdit || busy || !ready} onClick={editArea} data-testid="forgelink-image-edit-area">
          Edit this area
        </button>
      </div>
      {textDraft ? (
        <form className="fld-img-text" onSubmit={(event) => { event.preventDefault(); commitText(); }}>
          <input
            autoFocus
            value={textDraft.value}
            onChange={(event) => setTextDraft({ ...textDraft, value: event.target.value })}
            placeholder="Annotation text"
            aria-label="Annotation text"
            data-testid="forgelink-image-text-input"
          />
          <button type="submit" className="btn small primary">Place</button>
        </form>
      ) : null}
      <p className="fld-img-status" data-testid="forgelink-image-status">
        {status || (compare !== "off"
          ? "Compare uses the previous iteration. Mask and crop wait until compare is off."
          : "Scroll zooms. Fit and 100% reset the view. Rotate, flip, and crop stay in this viewer until you download. Edit this area sends the note through Continue editing.")}
      </p>
    </div>
  );
}
